import { z } from 'zod';

/** ADR-0050 / BR-PRD-63..67 — produto genérico de plataforma. Sem ean, preço, SKU ou place. */

export const CatalogGenericProductAttributeTypeSchema = z.enum([
  'NUMBER',
  'TEXT',
  'ENUM',
  'BOOLEAN',
]);

export const CatalogGenericProductAttributeSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[a-z][a-z0-9_]*$/),
  label: z.string().trim().min(1).max(120),
  type: CatalogGenericProductAttributeTypeSchema,
  required: z.boolean(),
  unit: z.string().trim().min(1).max(40).optional(),
  values: z.array(z.string().trim().min(1).max(80)).optional(),
});

export const CatalogGenericProductIdParamsSchema = z.object({
  id: z.uuid(),
});

const LifecycleIntSchema = z.number().int().min(0).nullable().optional();

export const CreateCatalogGenericProductBodySchema = z.strictObject({
  canonicalName: z.string().trim().min(1).max(255),
  categoryId: z.uuid(),
  ncmCode: z.string().trim().min(1).max(16).nullable().optional(),
  cestCode: z.string().trim().min(1).max(16).nullable().optional(),
  unitOfMeasureCode: z.string().trim().min(1).max(16).nullable().optional(),
  usefulLifeMonths: LifecycleIntSchema,
  usefulLifeMeters: LifecycleIntSchema,
  warrantyMonths: LifecycleIntSchema,
  warrantyMeters: LifecycleIntSchema,
  attributeSchema: z.array(CatalogGenericProductAttributeSchema).optional(),
});

export const UpdateCatalogGenericProductBodySchema = z.strictObject({
  canonicalName: z.string().trim().min(1).max(255).optional(),
  categoryId: z.uuid().optional(),
  ncmCode: z.string().trim().min(1).max(16).nullable().optional(),
  cestCode: z.string().trim().min(1).max(16).nullable().optional(),
  unitOfMeasureCode: z.string().trim().min(1).max(16).nullable().optional(),
  usefulLifeMonths: LifecycleIntSchema,
  usefulLifeMeters: LifecycleIntSchema,
  warrantyMonths: LifecycleIntSchema,
  warrantyMeters: LifecycleIntSchema,
  attributeSchema: z.array(CatalogGenericProductAttributeSchema).optional(),
});

export const ArchiveCatalogGenericProductBodySchema = z.preprocess(
  (value) => value ?? {},
  z.object({
    reason: z.string().trim().max(500).optional(),
  }),
);

export const ListCatalogGenericProductsQuerySchema = z.looseObject({
  q: z.string().trim().min(1).max(200).optional(),
  categoryId: z.uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const CatalogGenericProductResponseSchema = z.object({
  id: z.uuid(),
  canonicalName: z.string(),
  categoryId: z.uuid(),
  ncmCode: z.string().nullable(),
  cestCode: z.string().nullable(),
  unitOfMeasureCode: z.string().nullable(),
  usefulLifeMonths: z.number().int().nullable(),
  usefulLifeMeters: z.number().int().nullable(),
  warrantyMonths: z.number().int().nullable(),
  warrantyMeters: z.number().int().nullable(),
  attributeSchema: z.array(CatalogGenericProductAttributeSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable(),
  deletedByUserId: z.uuid().nullable(),
  deleteReason: z.string().nullable(),
});

export const CatalogGenericProductListItemSchema = CatalogGenericProductResponseSchema.extend({
  categoryName: z.string(),
});

export const CatalogGenericProductListResponseSchema = z.object({
  items: z.array(CatalogGenericProductListItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
    total: z.number().int().optional(),
  }),
});

export type CatalogGenericProductAttribute = z.infer<typeof CatalogGenericProductAttributeSchema>;
export type CatalogGenericProductIdParams = z.infer<typeof CatalogGenericProductIdParamsSchema>;
export type CreateCatalogGenericProductBody = z.infer<typeof CreateCatalogGenericProductBodySchema>;
export type UpdateCatalogGenericProductBody = z.infer<typeof UpdateCatalogGenericProductBodySchema>;
export type ArchiveCatalogGenericProductBody = z.infer<
  typeof ArchiveCatalogGenericProductBodySchema
>;
export type ListCatalogGenericProductsQuery = z.infer<typeof ListCatalogGenericProductsQuerySchema>;
export type CatalogGenericProductResponse = z.infer<typeof CatalogGenericProductResponseSchema>;
export type CatalogGenericProductListItem = z.infer<typeof CatalogGenericProductListItemSchema>;
export type CatalogGenericProductListResponse = z.infer<
  typeof CatalogGenericProductListResponseSchema
>;
